import { clamp, last } from '@sama/micro-dash';
import {
  addPoints,
  distanceBetween,
  dotProduct,
  scalePoint,
  subtractPoints,
} from '../../../../shared/misc-utilities/point-utils';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';

export function getInsertIndex(
  oldPoints: Point2D[],
  newPoint: Point2D,
  closedShape: boolean,
) {
  // We calculate the "distance info" for each possible insertion index. In
  // the common case all we need is the distance from the point to each line
  // segment, then we choose the insertion index that corresponds to the closest
  // line segment. However, at the ends of lines and around corners there can be
  // many points that are all the same distance to multiple insertion points.
  // In those cases, we break the tie by choosing the GREATER distance to the
  // line the segment is part of.
  const distanceInfos = calcDistanceInfos(oldPoints, newPoint, closedShape);
  let bestIndex = 0;
  distanceInfos.forEach((info, i) => {
    const improvement = distanceInfos[bestIndex].distance - info.distance;
    if (Math.abs(improvement) <= 1e-5) {
      if (info.tieBreaker > distanceInfos[bestIndex].tieBreaker) {
        bestIndex = i;
      }
    } else if (improvement > 0) {
      bestIndex = i;
    }
  });
  return bestIndex;
}

function calcDistanceInfos(
  oldPoints: Point2D[],
  newPoint: Point2D,
  closedShape: boolean,
) {
  const distanceInfos = oldPoints
    .slice(1)
    .map((endPoint, i) =>
      getDistanceInfoToSegment(newPoint, [oldPoints[i], endPoint]),
    );
  if (closedShape) {
    // Add segment between the last and the first point at the start, to make
    // indexes match insert point
    distanceInfos.unshift(
      getDistanceInfoToSegment(newPoint, [last(oldPoints), oldPoints[0]]),
    );
  } else {
    distanceInfos.unshift(getDistanceInfoToPoint(newPoint, oldPoints[0]));
    distanceInfos.push(getDistanceInfoToPoint(newPoint, last(oldPoints)));
  }

  return distanceInfos;
}

function getDistanceInfoToSegment(point: Point2D, segment: [Point2D, Point2D]) {
  const length = distanceBetween(...segment);
  if (length === 0) {
    return getDistanceInfoToPoint(point, segment[0]);
  }
  const lineVector = subtractPoints(segment[1], segment[0]);
  // Imagine projecting the point onto the line that includes
  // the segment. Calculate the distance between the first
  // endpoint and the projection, as a percentage of the
  // segment's length.
  const projectedDistanceFactor =
    dotProduct(subtractPoints(point, segment[0]), lineVector) / length ** 2;
  const projectedOnLine = addPoints(
    segment[0],
    scalePoint(lineVector, projectedDistanceFactor),
  );
  const projectedOnSegment = addPoints(
    segment[0],
    scalePoint(lineVector, clamp(projectedDistanceFactor, 0, 1)),
  );
  return {
    distance: distanceBetween(point, projectedOnSegment),
    tieBreaker: distanceBetween(point, projectedOnLine),
  };
}

function getDistanceInfoToPoint(p1: Point2D, p2: Point2D) {
  const distance = distanceBetween(p1, p2);
  return { distance, tieBreaker: distance };
}
